import { Accordion as RadixAccordion } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface AccordionItemData {
	/** Значение элемента — оно же ключ раскрытия. */
	value: string;
	question: ReactNode;
	answer: ReactNode;
}

export interface AccordionProps {
	items: readonly AccordionItemData[];
	/** Открытый по умолчанию элемент. Пусто — все свёрнуты. */
	defaultValue?: string;
	className?: string;
}

/**
 * Список «вопрос — ответ» со сворачиванием.
 *
 * Один открытый элемент за раз (`type="single"`, `collapsible`): смысл списка
 * в том, чтобы держать перед глазами вопросы, а не все ответы сразу.
 *
 * Высота анимируется через `--radix-accordion-content-height` — переменную
 * Radix считает сам; фиксированной высоты у ответов нет и быть не может,
 * тексты в 21 локали разной длины.
 */
export function Accordion({ items, defaultValue, className }: AccordionProps) {
	return (
		<RadixAccordion.Root
			type="single"
			collapsible
			{...(defaultValue ? { defaultValue } : {})}
			className={cn("flex flex-col", className)}
		>
			{items.map((item) => (
				<RadixAccordion.Item
					key={item.value}
					value={item.value}
					className="border-hairline border-b last:border-b-0"
				>
					<RadixAccordion.Header>
						<RadixAccordion.Trigger
							className={cn(
								"group flex w-full cursor-pointer items-center justify-between gap-4 py-3.5 text-left",
								"transition-colors duration-150 ease-smooth hover:text-fg-muted",
								"focus-ring",
							)}
						>
							<span className="t-row min-w-0">{item.question}</span>
							{/* Шеврон поворачивается, а не подменяется другой иконкой:
							    подмена дёргает базовую линию строки. */}
							<Icon
								name="ChevronDown"
								size={20}
								className="shrink-0 text-fg-muted transition-transform duration-200 ease-smooth group-data-[state=open]:rotate-180"
							/>
						</RadixAccordion.Trigger>
					</RadixAccordion.Header>
					<RadixAccordion.Content
						className={cn(
							"overflow-hidden",
							"data-[state=open]:[animation:iec-accordion-down_var(--dur-300)_var(--ease-smooth)]",
							"data-[state=closed]:[animation:iec-accordion-up_var(--dur-300)_var(--ease-smooth)]",
						)}
					>
						<div className="t-body pb-3.5 text-fg-secondary">{item.answer}</div>
					</RadixAccordion.Content>
				</RadixAccordion.Item>
			))}
		</RadixAccordion.Root>
	);
}
